import React, { useState } from 'react';
import { UserCircle } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import {
  buildMmMoneyIssuerTitle,
  formatMmMoneyDisplayTicker,
  parseMmMoneyIssuerId,
  type MmMoneyIssuerView,
} from '@vaiu/utils/mmMoney';
import { MerchantProfileSummary } from './MerchantProfileSummary';

export interface MmMoneyIssuerDisplayProps {
  /** Wire key `MM$` + issuer id */
  currencyKey: string;
  issuer?: Partial<MmMoneyIssuerView> | null;
  /** Optional platform hook — when omitted, summary dialog shows id only */
  fetchMerchantSummary?: (issuerUserId: string) => Promise<import('./MerchantProfileSummary').MerchantPublicSummary | null>;
  compact?: boolean;
  className?: string;
  /** When true, show MM$ ticker only (no issuer name/id suffix) — e.g. signed-in user's own issuer row. */
  tickerOnly?: boolean;
  /** Hide profile icon button (e.g. compact amount field beside input). */
  hideProfileAction?: boolean;
  /** Never show truncated issuer user id when name/username are unknown (e.g. token redeem). */
  omitIssuerIdFallback?: boolean;
}

export function MmMoneyIssuerDisplay({
  currencyKey,
  issuer,
  fetchMerchantSummary,
  compact,
  className = '',
  tickerOnly = false,
  hideProfileAction = false,
  omitIssuerIdFallback = false,
}: MmMoneyIssuerDisplayProps) {
  const issuerId = parseMmMoneyIssuerId(currencyKey) || '';
  const [summaryOpen, setSummaryOpen] = useState(false);

  const view: MmMoneyIssuerView = {
    issuerUserId: issuer?.issuerUserId || issuerId,
    currencyIconUrl: issuer?.currencyIconUrl,
    displayName: issuer?.displayName,
    name: issuer?.name,
    username: issuer?.username,
    backgroundUrl: issuer?.backgroundUrl,
  };

  const title = buildMmMoneyIssuerTitle(view, { omitIdFallback: omitIssuerIdFallback });
  const ticker = formatMmMoneyDisplayTicker();
  const showPlaceholderIcon = !tickerOnly && !view.currencyIconUrl;
  const showCustomIcon = Boolean(view.currencyIconUrl);

  return (
    <>
      <div className={`flex items-center gap-2 min-w-0 ${className}`}>
        {showCustomIcon ? (
          <img
            src={view.currencyIconUrl}
            alt=""
            className="h-6 w-6 rounded-sm object-cover border border-white/20 flex-shrink-0"
          />
        ) : showPlaceholderIcon ? (
          <div className="h-6 w-6 rounded-sm bg-muted flex items-center justify-center text-[10px] font-bold flex-shrink-0">
            MM
          </div>
        ) : null}
        <div className="flex flex-col min-w-0 flex-1">
          <span className={`font-semibold truncate ${compact ? 'text-xs' : 'text-sm'}`}>
            {ticker}
            {!tickerOnly && title ? (
              <>
                {' '}
                <span className="font-normal text-muted-foreground truncate">{title}</span>
              </>
            ) : null}
          </span>
        </div>
        {!hideProfileAction ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0"
          title="Merchant profile"
          onClick={(e) => {
            e.stopPropagation();
            setSummaryOpen(true);
          }}
        >
          <UserCircle className="h-4 w-4" />
        </Button>
        ) : null}
      </div>
      <MerchantProfileSummary
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        issuerUserId={issuerId}
        fetchSummary={fetchMerchantSummary}
        fallback={{
          displayName: view.displayName,
          name: view.name,
          username: view.username,
          currencyIconUrl: view.currencyIconUrl,
        }}
      />
    </>
  );
}
